import { useStore } from '@/store';
import type { StudyArm } from '@bioavail/shared-types';
import {
  RouteOfAdministration as ROA,
  DoseUnit as DU,
  ConcentrationUnit as CU,
  TimeUnit as TU,
  AnalyteType as AT,
} from '@bioavail/shared-types';
import { NumberInput } from '@/components/ui/NumberInput';
import { Select } from '@/components/ui/Select';
import { TextInput } from '@/components/ui/TextInput';

export function ArmFields({ arm }: { arm: StudyArm }) {
  const updateArm = useStore((s) => s.updateArm);

  const routeOptions = [
    { value: ROA.IV, label: 'IV' },
    { value: ROA.Oral, label: 'Oral' },
    { value: ROA.Subcutaneous, label: 'Subcutaneous' },
    { value: ROA.Intramuscular, label: 'Intramuscular' },
    { value: ROA.Sublingual, label: 'Sublingual' },
    { value: ROA.Topical, label: 'Topical' },
    { value: ROA.Other, label: 'Other' },
  ];
  const doseUnitOptions = [
    { value: DU.Mg, label: 'mg' },
    { value: DU.Ug, label: 'µg' },
    { value: DU.G, label: 'g' },
  ];
  const concOptions = [
    { value: CU.NgPerMl, label: 'ng/mL' },
    { value: CU.UgPerMl, label: 'µg/mL' },
    { value: CU.UgPerL, label: 'µg/L' },
    { value: CU.MgPerL, label: 'mg/L' },
    { value: CU.NmolPerL, label: 'nmol/L (molar)' },
  ];
  const timeOptions = [
    { value: TU.Hour, label: 'hours' },
    { value: TU.Minute, label: 'minutes' },
    { value: TU.Day, label: 'days' },
  ];
  const analyteOptions = [
    { value: AT.Parent, label: 'Parent' },
    { value: AT.Metabolite, label: 'Metabolite' },
  ];

  return (
    <div className="arm-fields">
      <span className={`role-badge ${arm.role}`}>{arm.role}</span>
      <TextInput
        label="Arm label"
        value={arm.label}
        onChange={(v) => updateArm(arm.id, { label: v })}
      />
      <Select
        label="Route"
        value={arm.route}
        onChange={(v) => updateArm(arm.id, { route: v as ROA })}
        options={routeOptions}
        hint="An IV reference arm is required for Absolute F."
      />
      <NumberInput
        label="Dose"
        value={arm.dose}
        min={0}
        step="any"
        onChange={(v) => updateArm(arm.id, { dose: v })}
        error={arm.dose <= 0 ? 'Dose must be a positive number.' : undefined}
      />
      <Select
        label="Dose unit"
        value={arm.doseUnit}
        onChange={(v) => updateArm(arm.id, { doseUnit: v as DU })}
        options={doseUnitOptions}
      />
      <Select
        label="Concentration unit"
        value={arm.concentrationUnit}
        onChange={(v) => updateArm(arm.id, { concentrationUnit: v as CU })}
        options={concOptions}
        hint="nmol/L is molar and cannot be mixed with mass/volume units across arms."
      />
      <Select
        label="Time unit"
        value={arm.timeUnit}
        onChange={(v) => updateArm(arm.id, { timeUnit: v as TU })}
        options={timeOptions}
      />
      <Select
        label="Analyte"
        value={arm.analyteType}
        onChange={(v) => updateArm(arm.id, { analyteType: v as AT })}
        options={analyteOptions}
        hint="Phase 1 fixed to parent; metabolite support in Phase 2."
      />
    </div>
  );
}
